'use client'

import { getCookie } from 'cookies-next'
import { motion } from 'framer-motion'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useEventListener, useMediaQuery, useOnClickOutside } from 'usehooks-ts'

import IconClose from '@/components/assets/icon_close.svg'
import { HTC_ACCESS_TOKEN_KEY } from '@/constants/auth'
import { useAuthContext } from '@/context/AuthContext'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useVSCSdkContext } from '@/context/VSCSdkContext'
import { cn } from '@/lib/utils'

type ViverseCommunityDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function ViverseCommunityDialog({ open, onOpenChange }: ViverseCommunityDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const [iframeContentWindow, setIframeContentWindow] = useState<Window | null>(null)
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const { viverseShareComponent, isMountedHeader } = useVSCSdkContext()
  const { appConfig } = useConfigsContext()
  const { isSignedIn } = useAuthContext()

  const communityWebUrl = appConfig?.features?.community_web_url

  const postCommunityInitAuth = useCallback(async () => {
    const accessTokenCookie = (await getCookie(HTC_ACCESS_TOKEN_KEY)) || ''
    const postData = {
      method: 'initialize-auth',
      payload: { data: accessTokenCookie },
    }
    iframeContentWindow!.postMessage(postData, communityWebUrl!)
  }, [communityWebUrl, iframeContentWindow])

  const postCommunityFullScreen = useCallback(() => {
    const postData = {
      method: 'community-web-fullscreen',
      payload: { state: !isDesktop },
    }
    iframeContentWindow!.postMessage(postData, communityWebUrl!)
  }, [communityWebUrl, isDesktop, iframeContentWindow])

  useEffect(() => {
    if (open && iframeContentWindow && isMountedHeader) {
      postCommunityFullScreen()
      viverseShareComponent?.showCommunityNotify(false)
    }
  }, [open, iframeContentWindow, viverseShareComponent, isMountedHeader, postCommunityFullScreen])

  useEffect(() => {
    if (isSignedIn && iframeContentWindow && viverseShareComponent) {
      postCommunityInitAuth()
      postCommunityFullScreen()
    }
  }, [
    viverseShareComponent,
    iframeContentWindow,
    isSignedIn,
    postCommunityFullScreen,
    postCommunityInitAuth,
  ])

  useOnClickOutside(dialogRef as RefObject<HTMLDivElement>, () => {
    onOpenChange(false)
  })

  useEventListener('message', (event) => {
    if (event.data?.source?.startsWith('react')) {
      // skip react devtools log
      return
    }

    if (!viverseShareComponent?.showCommunityNotify) {
      return
    }

    switch (event.data.method) {
      case 'friend-list-modified':
      case 'new-note':
      case 'friend-request-list-modified': {
        if (!open) viverseShareComponent.showCommunityNotify(true)
        break
      }
      case 'close-community-web': {
        onOpenChange(false)
        break
      }
    }
  })

  return createPortal(
    <motion.div
      ref={dialogRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: open ? 1 : 0 }}
      transition={{
        ease: 'easeOut',
      }}
    >
      <div
        className={cn(
          'border-vive-primary-70 bg-vive-popup fixed top-1/2 left-1/2 z-10000 flex size-full -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-2xl border-2 p-1 px-8! outline-hidden lg:right-4 lg:left-auto lg:max-h-[calc(100dvh-176px)] lg:w-[410px] lg:translate-x-0',
          {
            hidden: !open,
          },
        )}
      >
        <div className="absolute top-0">
          <div className="lg:header-4 relative flex h-[80px] w-full items-center justify-center py-[18px] text-center lg:text-2xl">
            <IconClose className="absolute right-0 lg:hidden" />
          </div>
        </div>
        {isSignedIn && isMountedHeader && (
          <iframe
            allow="camera *; microphone *"
            className={cn('absolute top-0 left-0 size-full flex-1', {
              hidden: !open,
            })}
            ref={iframeRef}
            src={communityWebUrl}
            frameBorder="0"
            allowFullScreen
            onLoad={() => {
              const iframeWindow = iframeRef.current?.contentWindow
              setIframeContentWindow(iframeWindow!)
            }}
          />
        )}
      </div>
    </motion.div>,
    document.body,
  )
}

export default ViverseCommunityDialog
